import type { DataIndexAttributeRow, DataIndexEventRow } from '@/services/db/data-index/entity';
import type { Source } from '@customerio/hydra-ui-api';
import {
  resolveAttributeDescription,
  resolveEventDescription,
  type ReservedDescriptionTerms,
} from './reserved-descriptions';
import type { DataIndexItemKind } from './types';
import { displayNameOf } from './item';

/** The privacy level the backend uses for an attribute someone marked sensitive. */
const SENSITIVE = 1;

/** One row of either browser, in the shape the table reads. */
export type DataIndexTableRow = {
  id: string;
  /** What the row is called on screen: an event's ingested casing, otherwise the id. */
  displayName: string;
  /**
   * The description the row shows: what the workspace wrote, or the copy
   * Customer.io reserves for a name it owns. Resolved once here rather than
   * per cell, because editing a row starts from what it shows.
   */
  description: string | null;
  /** Only an attribute can be marked sensitive; an event never is. */
  isSensitive: boolean;
  tagIds: readonly number[];
  sources: readonly Source[];
};

/** An event row names itself by its ingested casing; an attribute row by its id. */
function rowDisplayName(row: DataIndexAttributeRow | DataIndexEventRow): string {
  return 'metadata' in row ? displayNameOf(row.id, row.metadata?.last_seen_as) : row.id;
}

/**
 * One wire row as the table reads it.
 *
 * An attribute's metadata arrives under a different key from an event's, and
 * only an attribute carries a privacy level, so the two shapes are reconciled
 * here rather than at every cell.
 */
export function toTableRow(
  row: DataIndexAttributeRow | DataIndexEventRow,
  kind: DataIndexItemKind,
  terms: ReservedDescriptionTerms,
): DataIndexTableRow {
  const metadata = 'metadata_summary' in row ? row.metadata_summary : row.metadata;

  return {
    id: row.id,
    displayName: rowDisplayName(row),
    description:
      kind === 'event'
        ? (resolveEventDescription(row.id, metadata) ?? null)
        : resolveAttributeDescription(row.id, kind, metadata, terms),
    isSensitive: metadata != null && 'privacy_level' in metadata && metadata.privacy_level === SENSITIVE,
    tagIds: metadata?.tag_ids ?? [],
    sources: metadata?.sources ?? [],
  };
}
